import { useState, useEffect, useContext } from 'react'
import styled from 'styled-components/macro'
import tw from 'twin.macro'
// import Web3Context from 'context/Web3Context'
import { GeyserContext } from 'context/GeyserContext'
import { StatsContext } from 'context/StatsContext'
import { safeNumeral } from 'utils/numeral'
import TooltipTable from 'components/TooltipTable'
import { GeyserStatsBox } from './GeyserStatsBox'
import { GET_APY_STAKE_MSG, GET_APY_WARN_MSG, GET_REWARD_MULTIPLIER_MSG, CURRENT_REWARDS_MSG } from '../../constants'

export const MyStats = () => {
  // const { ready } = useContext(Web3Context)
  const {
    userStats: { apy, currentMultiplier, maxMultiplier, currentReward },
    vaultStats: { currentStake },
    geyserStats: { duration, hasMultiplier, multiplierDurationInDays },
  } = useContext(StatsContext)
  const {
    selectedGeyserInfo: {
      geyser: selectedGeyser,
      rewardTokenInfo: { symbol: rewardTokenSymbol, price: rewardTokenPrice },
      stakingTokenInfo: { price: stakingTokenPrice },
    },
    getGeyserConfig,
    stakeAPYs,
  } = useContext(GeyserContext)

  const [lpAPY, setLPAPY] = useState<number>(0)
  const [geyserAPY, setGeyserAPY] = useState<number>(0)
  const [finalAPY, setFinalAPY] = useState<number>(0)
  useEffect(() => {
    (async () => {
    if (!selectedGeyser) {
      return
    }
    const config = getGeyserConfig(selectedGeyser.id)
    const lpAPYNew = (stakeAPYs.lp && config && config.lpRef && stakeAPYs.lp[config.lpRef]) || 0
    const geyserAPYGlobal = stakeAPYs.geysers && config && stakeAPYs.geysers[config.slug]
    // const geyserAPYNew = ready ? apy : geyserAPYGlobal || apy
    // NOTE: just showing the global APY as a guideline for most users.
    let geyserAPYNew = geyserAPYGlobal || apy
    if (duration === 0) {
      geyserAPYNew = 0
    }
    setLPAPY(lpAPYNew)
    setGeyserAPY(geyserAPYNew)
    setFinalAPY(Math.min(geyserAPYNew + lpAPYNew, 100000))
  })()}, [selectedGeyser, apy, duration])

  // TODO: handle bonus tokens
  const baseRewards = currentReward * rewardTokenPrice
  return (
    <MyStatsContainer>
      <Header>My Stats </Header>
      <GeyserStatsContainer>
        <GeyserStatsBoxContainer>
          <GeyserStatsBox
            containerClassName="w-1/2"
            name="APY"
            value={finalAPY}
            units="%"
            interpolate={(val) => safeNumeral(val, '0.00%').slice(0, val > 100 ? -4 : -1)}
            tooltipMessage={{
              title: 'Staking Yield',
              body: (
                <div>
                  {GET_APY_STAKE_MSG()}
                  <TooltipTable
                    rows={[
                      { label: 'LP APY', value: lpAPY ? safeNumeral(lpAPY, '0.00%') : 'N.A' },
                      { label: 'Geyser drip rate', value: safeNumeral(geyserAPY, '0.00%') },
                      // { label: 'Bonus', value: '0.00%' },
                    ]}
                    totalLabel="Combined APY"
                    totalValue={safeNumeral(finalAPY, '0.00%')}
                  />
                  <ApyWarning>{GET_APY_WARN_MSG()}</ApyWarning>
                </div>
              ),
            }}
          />

          {hasMultiplier ? (
            <GeyserStatsBox
              containerClassName="w-1/2"
              name="Multiplier"
              value={currentMultiplier}
              units="x"
              interpolate={(val) => safeNumeral(val, '0.0')}
              tooltipMessage={{
                title: 'Bonus Multiplier',
                body: GET_REWARD_MULTIPLIER_MSG({
                  days: safeNumeral(multiplierDurationInDays || 30, '0'),
                  multiplier: safeNumeral(maxMultiplier || 3, '0.0'),
                }),
              }}
            />
          ) : (
            <></>
          )}
        </GeyserStatsBoxContainer>

        <GeyserStatsBoxContainer>
          <GeyserStatsBox
            containerClassName="w-full"
            name="Current Stake"
            value={currentStake * stakingTokenPrice}
            units="USD"
            interpolate={(val) => safeNumeral(val, '0,0.00')}
          />
        </GeyserStatsBoxContainer>

        <GeyserStatsBoxContainer>
          <GeyserStatsBox
            containerClassName="w-full"
            name="Current Rewards"
            value={baseRewards}
            units="USD"
            interpolate={(val) => safeNumeral(val, '0,0.00')}
            tooltipMessage={{
              title: 'Current Rewards',
              body: (
                <div>
                  {CURRENT_REWARDS_MSG()}
                  <TooltipTable
                    rows={[
                      {
                        label: `${rewardTokenSymbol} (${safeNumeral(currentReward, '0.00')})`,
                        value: `${safeNumeral(baseRewards, '0,0.00')} USD`,
                      },
                      { label: 'bonus', value: `${safeNumeral(0, '0,0.00')} USD` },
                    ]}
                    totalLabel="Total"
                    totalValue={`${safeNumeral(baseRewards, '0,0.00')} USD`}
                  />
                </div>
              ),
            }}
          />
        </GeyserStatsBoxContainer>
      </GeyserStatsContainer>
    </MyStatsContainer>
  )
}

const MyStatsContainer = styled.div`
  ${tw`px-5 my-5 pr-0 border-r-2 border-lightGray`}
`

const Header = styled.h3`
  ${tw`uppercase flex text-radicalRed font-medium sm:pl-3`}
`

const GeyserStatsContainer = styled.div`
  ${tw`mt-4`}
  ${tw`sm:mt-0`}
`

const GeyserStatsBoxContainer = styled.div`
  ${tw`flex mt-4 sm:mt-3`}
`

const ApyWarning = styled.div`
  ${tw`mt-4 text-xs italic leading-4`}
`
